import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import { Badge, Box, IconButton } from '@mui/material';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { BETSLIP_WIDTH } from '../../constants';
import { checkIsWaysEventForReofferedBetslip } from '../../helpers';
import { useReofferedBetslip } from '../../hooks/useReofferedBetslip';
import ReofferedBetslipCountdown from '../atoms/ReofferedBetslipCountdown';
import ReofferedBetslipItem from '../molecules/ReofferedBetslipItem';
import ReofferedBetBuilderItem from '../molecules/ReofferedBetBuilderItem';
import ReofferedBetslipActions from '../molecules/ReofferedBetslipActions';
import OddsDisplay from '../atoms/OddsDisplay';
import { CurrencySymbol } from '../atoms/CurrencySymbol';

import { mergeSx } from '../../utils/styles';

const styles = {
  slipCount: { fontWeight: 700 },
  bettingType: { color: 'neutral.600', pl: 1.5, pr: 0.25, py: 1, textAlign: 'left' },
  infoRow: { alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 },
  centeredRow: { alignItems: 'center' },
  details: { color: 'neutral.600' },
  summary: { px: 1.5, py: 1 },
  summaryText: { color: 'neutral.600', textAlign: 'right' },
  previousPayoutSpacing: { px: 0.5 },

  color: (color: string) => ({ color }),
  stake: {
    textAlign: 'right',
    fontWeight: 600,
  },
  drawer: {
    zIndex: 1400,
    textAlign: 'center',
    '& .MuiPaper-root': {
      width: {
        xs: '100%',
        sm: BETSLIP_WIDTH,
      },
      backgroundColor: 'neutral.50',
      borderRadius: {
        xs: 0,
        sm: '4px 4px 0 0',
      },
      margin: 'auto',
      mb: {
        xs: 0,
        md: 4,
      },
    },
    '& form': { maxHeight: '95vh' },
  },
  betStack: {
    flexGrow: 1,
    overflowY: 'auto',
    maxHeight: 566,
  },
  bettingSlipCountSection: {
    p: 1.5,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: 'primary.50',
    color: 'neutral.700',
    fontWeight: 'bold',
  },
  timerWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  bettingSlipNavigationWrapper: {
    display: 'flex',
  },
  navigationBtn: {
    p: 0,
  },
  bettingSlipCountBadge: {
    mr: 1,
    '& .MuiBadge-badge': {
      position: 'relative',
      transform: 'none',
      WebkitTransform: 'none',
    },
  },
  bankerOrNormalText: {
    px: 1.5,
    py: 1,
    color: 'primary.600',
    textAlign: 'right',
  },
  betslipInfo: {
    backgroundColor: 'neutral.100',
    borderBottom: '1px solid #D9D9D9',
    py: 1,
    pl: 1.5,
    height: 40,
  },
  betslipAmountField: {
    height: 42,
    flexDirection: 'row',
    backgroundColor: 'white',
    ml: 1.5,
    mt: 0.25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    px: 2,
    gap: 0.5,
    borderBottom: '1px solid',
    borderColor: 'primary.main',
    '& h5': {
      fontFamily: 'Open Sans',
    },
  },
  previousValue: {
    textDecoration: 'line-through',
    color: 'neutral.800',
    opacity: 0.5,
  },
} as const;

const ReofferedBetslipDrawer = () => {
  const {
    open,
    currentBetslip,
    betslip,
    reofferedFromBetslip,
    bets,
    previousBets,
    betBuilderGroups,
    isBanker,
    totalOddsColor,
    totalOddsChanged,
    stakeAmountColor,
    stakeAmountChanged,
    maxPayoutColor,
    maxPayoutChanged,
    localizedBettingType,
    showBonusData,
    betslipHeaderTitle,
    currentIndex,
    totalSlips,
    isPrevDisabled,
    goToPrev,
    goToNext,
    t,
  } = useReofferedBetslip();

  return (
    <>
      <Drawer anchor='bottom' elevation={0} open={open} sx={styles.drawer}>
        <Box sx={styles.bettingSlipCountSection}>
          <Badge badgeContent={totalSlips} color='primary' sx={styles.bettingSlipCountBadge} />
          <Box sx={styles.timerWrapper}>
            {betslipHeaderTitle}
            {betslip?.createdAt && (
              <ReofferedBetslipCountdown createdAt={betslip?.createdAt} ticketId={currentBetslip} />
            )}
            <Box sx={styles.bettingSlipNavigationWrapper}>
              <IconButton disabled={isPrevDisabled} sx={styles.navigationBtn} onClick={goToPrev}>
                <NavigateBefore />
              </IconButton>

              <Typography sx={styles.slipCount}>
                {currentIndex + 1}/{totalSlips}
              </Typography>
              <IconButton sx={styles.navigationBtn} onClick={goToNext}>
                <NavigateNext />
              </IconButton>
            </Box>
          </Box>
        </Box>
        <Divider />
        <Typography sx={styles.bankerOrNormalText} variant='h6'>
          {isBanker ? t('banker') : t('normal')}
        </Typography>
        <Divider />
        <Stack sx={styles.betStack}>
          {bets.map((bet, index) => (
            <ReofferedBetslipItem
              key={bet.outcome.id}
              bet={bet}
              previousBet={previousBets[index]}
              showBankers={isBanker}
              isWaysEvent={checkIsWaysEventForReofferedBetslip(bet, bets)}
              bets={bets}
            />
          ))}
          {betBuilderGroups.map((group) => (
            <ReofferedBetBuilderItem key={group.id} group={group} />
          ))}
        </Stack>
        <Divider />
        {betslip && (
          <>
            {betslip?.bettingSlipType !== 'Single' && (
              <Typography variant='body3' sx={styles.bettingType}>
                {betslip?.bettingSlipType === 'Multiple' ? t('multiple') : t('system')}
              </Typography>
            )}
            <Stack direction='row' sx={mergeSx(styles.infoRow, styles.betslipInfo)}>
              <Stack spacing={0.5} direction='row' sx={styles.centeredRow}>
                <Typography variant='h5' sx={styles.details}>
                  {localizedBettingType}
                </Typography>
                {totalOddsChanged && (
                  <OddsDisplay
                    value={reofferedFromBetslip?.totalOdds ?? ''}
                    variant='h5'
                    sx={mergeSx(styles.details, styles.previousValue)}
                  />
                )}
                <OddsDisplay value={betslip?.totalOdds ?? ''} variant='h5' sx={styles.color(totalOddsColor)} />
              </Stack>
              <Stack direction='row' sx={styles.centeredRow}>
                <Typography variant='body2' sx={styles.details}>
                  {betslip?.combinationsCount}x
                </Typography>
                <Box sx={styles.betslipAmountField}>
                  {stakeAmountChanged && (
                    <Typography variant='h5' sx={styles.previousValue}>
                      {reofferedFromBetslip?.stakeAmountPerCombination?.toFixed(2)}
                    </Typography>
                  )}
                  <Typography variant='h5' sx={mergeSx(styles.stake, styles.color(stakeAmountColor))}>
                    {betslip?.stakeAmountPerCombination?.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </>
        )}
        <Divider />
        <Stack spacing={0.25} sx={styles.summary}>
          {showBonusData && (
            <Typography variant='body3' sx={styles.summaryText}>
              {t('bonus')}: {betslip?.bonus?.bonusPercentage}% (
              <CurrencySymbol />
              {betslip?.bonus?.bonusAmount.toFixed(2)})
            </Typography>
          )}
          <Typography variant='body3' sx={styles.summaryText}>
            {t('maxPossibleWinning')}:{' '}
            {maxPayoutChanged && (
              <Typography
                component='span'
                variant='body3'
                sx={mergeSx(styles.previousPayoutSpacing, styles.previousValue)}
              >
                <CurrencySymbol />
                {reofferedFromBetslip?.maxPayout.toFixed(2)}
              </Typography>
            )}
            <Typography component='span' variant='body3' sx={styles.color(maxPayoutColor)}>
              <CurrencySymbol />
              {betslip?.maxPayout.toFixed(2)}
            </Typography>
          </Typography>
        </Stack>
        <ReofferedBetslipActions currentBetslip={currentBetslip} />
      </Drawer>
    </>
  );
};

export default ReofferedBetslipDrawer;
